import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './ProductDisplay.css'
import { ShopContext } from '../../Context/ShopContext'

const ProductDisplay = (props) => {
    const { product } = props;
    const { addToCart } = useContext(ShopContext);
    const [selectedSize, setSelectedSize] = useState('');
    const [sizeError, setSizeError] = useState('');
    const [selectedImage, setSelectedImage] = useState(0);
    const navigate = useNavigate();

    // Add safety checks for the product data
    if (!product) {
        return <div className='productdisplay'>Product not found</div>;
    }

    if (!product.name) {
        console.error("Invalid product data:", product);
        return <div className='productdisplay'>Invalid product data</div>;
    }

    // Get all images
    const getAllImages = () => {
        const images = [];
        
        if (product.main_image) {
            images.push(product.main_image);
        }
        
        if (product.additional_images && product.additional_images.length > 0) {
            images.push(...product.additional_images.filter(img => img));
        }
        
        if (images.length === 0 && product.images && product.images.length > 0) {
            images.push(...product.images.filter(img => img));
        }
        
        if (images.length === 0 && product.image) {
            images.push(product.image);
        }
        
        return images;
    };

    const allImages = getAllImages();

    // Get available sizes - use product.sizes if available, otherwise use defaults
    const getAvailableSizes = () => {
        if (product.sizes && product.sizes.length > 0) {
            return product.sizes;
        }
        
        // Default sizes based on product type
        if (product.productType === 'shoes') {
            // Shoe sizes (38-49)
            return Array.from({length: 12}, (_, i) => (38 + i).toString());
        } else {
            // Clothing sizes
            return ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        }
    };

    const availableSizes = getAvailableSizes();

    // Handle size selection
    const handleSizeSelect = (size) => {
        setSelectedSize(size);
        setSizeError('');
    }

    // Handle image selection
    const handleImageSelect = (index) => {
        setSelectedImage(index);
    }

    // Handle add to cart with validation - UPDATED: No redirect
    const handleAddToCart = async () => {
        if (!selectedSize) {
            setSizeError('Please select a size');
            return;
        }
        
        try {
            // Add to cart with product ID and size only (no color)
            const success = await addToCart(product.id, selectedSize, '');
            
            if (success) {
                // Only add to cart, do NOT redirect
                // You can optionally show a success message here
                console.log('Item added to cart successfully');
            } else {
                console.error('Failed to add item to cart');
                setSizeError('Failed to add item to cart. Please try again.');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setSizeError('An error occurred while adding to cart');
        }
    }

    // Handle "Buy Now" - Add to cart and immediately redirect to cart page - UPDATED
    const handleBuyNow = async () => {
        if (!selectedSize) {
            setSizeError('Please select a size');
            return;
        }
        
        try {
            // Add to cart with product ID and size only (no color)
            const success = await addToCart(product.id, selectedSize, '');
            
            if (success) {
                // Redirect to cart page
                navigate('/cart');
            } else {
                console.error('Failed to add item to cart');
                setSizeError('Failed to add item to cart. Please try again.');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setSizeError('An error occurred while adding to cart');
        }
    }

    // Get product type display name
    const getProductTypeDisplay = () => {
        return product.productType === 'shoes' ? 'Shoes' : 'Clothing';
    }

    return (
        <div className='productdisplay'>
            <div className="productdisplay-left">
                <div className="productdisplay-img-list">
                    {allImages.map((image, index) => (
                        <img 
                            key={index}
                            src={image} 
                            alt={`${product.name} view ${index + 1}`}
                            className={selectedImage === index ? 'active' : ''}
                            onClick={() => handleImageSelect(index)}
                        />
                    ))}
                </div>
                <div className="productdisplay-img">
                    <img 
                        className='productdisplay-main-img' 
                        src={allImages[selectedImage] || product.image} 
                        alt={product.name} 
                    />
                </div>
            </div>
            <div className="productdisplay-right">
                <h1>{product.name}</h1>
                
                {/* Product Type Badge */}
                <div className="product-type-badge">
                    <span className={`badge ${product.productType || 'clothing'}`}>
                        {getProductTypeDisplay()}
                    </span>
                </div>
                
               
                <div className="productdisplay-right-prices">
                    <div className="productdisplay-right-price-old">Rs {product.old_price}</div>
                    <div className="productdisplay-right-price-new">Rs {product.new_price}</div>
                </div>
                <div className="productdisplay-right-description">
                    {product.description || "Product description not available."}
                </div>
                
                {/* Additional Details */}
                <div className="product-details-grid">
                    {product.material && (
                        <div className="detail-item">
                            <span className="detail-label">Material:</span>
                            <span className="detail-value">{product.material}</span>
                        </div>
                    )}
                    {product.brand && (
                        <div className="detail-item">
                            <span className="detail-label">Brand:</span>
                            <span className="detail-value">{product.brand}</span>
                        </div>
                    )}
                </div>

                {/* Size Selection Section */}
                <div className="productdisplay-right-size">
                    <h1>Select Size {product.productType === 'shoes' ? '(EU)' : ''}</h1>
                    <div className={`productdisplay-right-sizes ${product.productType === 'shoes' ? 'shoe-sizes' : 'clothing-sizes'}`}>
                        {availableSizes.map((size) => (
                            <div 
                                key={size}
                                className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                                onClick={() => handleSizeSelect(size)}
                            >
                                {size}
                            </div>
                        ))}
                    </div>
                    {sizeError && <div className="error-message">{sizeError}</div>}
                </div>

                <div className="product-action-buttons">
                    <button 
                        className="btn-add-to-cart" 
                        onClick={handleAddToCart}
                    >
                        ADD TO CART
                    </button>
                    
                    <button 
                        className="btn-buy-now"
                        onClick={handleBuyNow}
                    >
                        BUY NOW
                    </button>
                </div>
                
                <p className='productdisplay-right-category'>
                    <span>Category :</span> {product.category || "Not specified"}
                </p>
                <p className='productdisplay-right-category'>
                    <span>Type :</span> {getProductTypeDisplay()}
                </p>
                {product.care_instructions && (
                    <div className="care-instructions">
                        <span>Care Instructions:</span> {product.care_instructions}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductDisplay
